import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  LinearProgress,
  Button,
  Alert,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as CommissionIcon,
  Assessment as SalesIcon,
  Schedule as TimeIcon,
  Star as StarIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  TableChart as ReportsIcon
} from '@mui/icons-material';
// Mock data removed - will fetch from API

const RepDashboard = ({ currentUser }) => {
  const [timeFilter, setTimeFilter] = useState('current_month');
  
  // Temporary placeholders - will be fetched from API
  const mockOrders = [];
  const mockClinics = [];
  const mockCompanies = [];
  
  // Filter orders for this rep's clinics
  const repOrders = useMemo(() => {
    if (!currentUser?.clinicIds) return [];
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return mockOrders.filter(order => {
      // Check if order belongs to rep's clinics
      const orderClinic = mockClinics.find(clinic => 
        clinic.companyId === order.customerId || clinic.id === order.customerId
      );
      if (!orderClinic || !currentUser.clinicIds.includes(orderClinic.id)) {
        return false;
      }
      
      const orderDate = new Date(order.orderDate);
      const orderMonth = orderDate.getMonth();
      const orderYear = orderDate.getFullYear();
      
      switch (timeFilter) {
        case 'current_month':
          return orderMonth === currentMonth && orderYear === currentYear;
        case 'previous_month':
          const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          return orderMonth === prevMonth && orderYear === prevYear;
        case 'current_quarter':
          const currentQuarter = Math.floor(currentMonth / 3);
          const orderQuarter = Math.floor(orderMonth / 3);
          return orderQuarter === currentQuarter && orderYear === currentYear;
        case 'previous_quarter':
          const prevQuarter = Math.floor(currentMonth / 3) - 1;
          const targetQuarter = prevQuarter < 0 ? 3 : prevQuarter;
          const targetYear = prevQuarter < 0 ? currentYear - 1 : currentYear;
          const orderQ = Math.floor(orderMonth / 3);
          return orderQ === targetQuarter && orderYear === targetYear;
        case 'ytd':
          return orderYear === currentYear;
        default:
          return true;
      }
    });
  }, [currentUser, timeFilter]);

  // Calculate metrics
  const totalSales = repOrders.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalOrders = repOrders.length;
  const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
  
  // Commission calculation
  const commissionThreshold = currentUser?.commissionThreshold || 200000;
  const commissionRate = currentUser?.commissionRate || 25;
  const commissionEarned = totalSales >= commissionThreshold;
  const commissionAmount = commissionEarned ? (totalSales * commissionRate / 100) : 0;
  const commissionProgress = Math.min((totalSales / commissionThreshold) * 100, 100);
  
  // Current month data for progress tracking
  const currentMonthData = {
    sales: totalSales,
    commissionAmount,
    commissionEarned,
    isLocked: false
  };
  
  // Get unique clinics from orders
  const activeClinics = useMemo(() => {
    const clinicIds = [...new Set(repOrders.map(order => {
      const clinic = mockClinics.find(c => 
        c.companyId === order.customerId || c.id === order.customerId
      );
      return clinic?.id;
    }).filter(Boolean))];
    
    return mockClinics.filter(clinic => clinicIds.includes(clinic.id));
  }, [repOrders]);

  const MetricCard = ({ title, value, subtitle, icon, color = '#0d9488', trend }) => (
    <Card sx={{ 
      height: '100%', 
      position: 'relative', 
      overflow: 'visible',
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      <CardContent sx={{ p: 3, position: 'relative' }}>
        {/* Subtle background gradient */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: `linear-gradient(135deg, ${color}08, ${color}03)`,
          borderRadius: '0 12px 12px 0'
        }} />
        
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color, mb: 1, lineHeight: 1.1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 1, bgcolor: '#f0fdf4', borderRadius: 2, width: 'fit-content' }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#16a34a', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 700, fontSize: '0.75rem' }}>
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ 
            bgcolor: color, 
            width: 64, 
            height: 64,
            boxShadow: `0 8px 24px ${color}40`,
            border: `3px solid ${color}20`
          }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case 'current_month': return 'Current Month';
      case 'previous_month': return 'Previous Month';
      case 'current_quarter': return 'Current Quarter';
      case 'previous_quarter': return 'Previous Quarter';
      case 'ytd': return 'Year to Date';
      default: return 'All Time';
    }
  };

  return (
    <Box sx={{ p: 3, background: 'linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%)', minHeight: '100vh' }}>
      {/* Professional Header */}
      <Box sx={{ 
        mb: 4, 
        p: 4, 
        background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
        borderRadius: 3,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(13, 148, 136, 0.3)'
      }}>
        {/* Decorative Elements */}
        <Box sx={{ 
          position: 'absolute', 
          top: -50, 
          right: -50, 
          width: 150, 
          height: 150, 
          borderRadius: '50%', 
          bgcolor: 'rgba(255, 255, 255, 0.1)' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: -30, 
          left: -30, 
          width: 100, 
          height: 100, 
          borderRadius: '50%', 
          bgcolor: 'rgba(255, 255, 255, 0.05)' 
        }} />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 3, width: 56, height: 56, border: '3px solid rgba(255, 255, 255, 0.3)' }}>
              <TrendingUpIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                Sales Dashboard
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Real-time Performance Analytics
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Live Data"
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.3)',
                color: '#10b981',
                fontWeight: 700,
                border: '2px solid #10b981',
                '& .MuiChip-icon': { color: '#10b981' }
              }}
              icon={<CheckIcon />}
            />
            <Chip
              label="Commission Tracking"
              sx={{
                bgcolor: 'rgba(59, 130, 246, 0.3)',
                color: '#3b82f6',
                fontWeight: 700,
                border: '2px solid #3b82f6'
              }}
              icon={<StarIcon />}
            />
          </Box>
          
          <Typography variant="body1" sx={{ 
            mt: 2, 
            opacity: 0.95, 
            fontWeight: 500,
            maxWidth: '600px',
            lineHeight: 1.6
          }}>
            Monitor your territory performance, track commission progress, and analyze sales trends in real-time.
          </Typography>
        </Box>
      </Box>
        
      {/* Time Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timeFilter}
              label="Time Period"
              onChange={(e) => setTimeFilter(e.target.value)}
            >
              <MenuItem value="current_month">Current Month</MenuItem>
              <MenuItem value="previous_month">Previous Month</MenuItem>
              <MenuItem value="current_quarter">Current Quarter</MenuItem>
              <MenuItem value="previous_quarter">Previous Quarter</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
            </Select>
          </FormControl>
        <Typography variant="body2" color="text.secondary">
          Showing data for: <strong>{getTimeFilterLabel()}</strong>
        </Typography>
      </Box>

      {/* Commission Status Alert */}
      {timeFilter === 'current_month' && (
        <Alert 
          severity={commissionEarned ? "success" : "info"} 
          sx={{ mb: 3 }}
          icon={commissionEarned ? <CheckIcon /> : <TimeIcon />}
        >
          <Typography variant="body2">
            {commissionEarned 
              ? `🎉 Congratulations! You've earned ${commissionRate}% commission this month!`
              : `You need $${(commissionThreshold - totalSales).toLocaleString()} more in sales to earn ${commissionRate}% commission.`
            }
            {currentUser?.isCommissionLocked && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                <LockIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Commission locked for this period
              </Box>
            )}
          </Typography>
        </Alert>
      )}

      {/* Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Sales"
            value={`$${totalSales.toLocaleString()}`}
            subtitle={`${totalOrders} orders`}
            icon={<SalesIcon />}
            color="#0d9488"
            trend="+12% vs last period"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Commission Earned"
            value={`$${commissionAmount.toLocaleString()}`}
            subtitle={`${commissionRate}% rate`}
            icon={<CommissionIcon />}
            color={commissionEarned ? "#10b981" : "#6b7280"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Clinics"
            value={activeClinics.length}
            subtitle="Ordering this period"
            icon={<StarIcon />}
            color="#0f766e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Order Value"
            value={`$${avgOrderValue.toLocaleString()}`}
            subtitle="Per transaction"
            icon={<TrendingUpIcon />}
            color="#0d9488"
          />
        </Grid>
      </Grid>

      {/* Enhanced Commission Progress */}
      {timeFilter === 'current_month' && (
        <Card sx={{ 
          mb: 4,
          borderRadius: 3,
          background: commissionEarned 
            ? 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
            : 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
          border: commissionEarned ? '2px solid #10b981' : '2px solid #f59e0b',
          boxShadow: commissionEarned 
            ? '0 8px 32px rgba(16, 185, 129, 0.2)'
            : '0 8px 32px rgba(245, 158, 11, 0.2)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ 
                bgcolor: commissionEarned ? '#10b981' : '#f59e0b',
                mr: 3,
                width: 56,
                height: 56,
                boxShadow: commissionEarned 
                  ? '0 8px 24px rgba(16, 185, 129, 0.3)'
                  : '0 8px 24px rgba(245, 158, 11, 0.3)'
              }}>
                {commissionEarned ? <CheckIcon /> : <TimeIcon />}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: commissionEarned ? '#16a34a' : '#d97706', mb: 0.5 }}>
                  Commission Progress
                </Typography>
                <Typography variant="body1" sx={{ color: '#374151', fontWeight: 500 }}>
                  {commissionEarned ? 'Congratulations! Commission earned!' : `${commissionThreshold - (currentMonthData?.sales || 0) > 0 ? '$' + (commissionThreshold - (currentMonthData?.sales || 0)).toLocaleString() + ' to go' : 'Target reached!'}`}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  Progress to {commissionRate}% Commission
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: commissionEarned ? '#16a34a' : '#d97706' }}>
                  {commissionProgress.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={commissionProgress} 
                sx={{ 
                  height: 12, 
                  borderRadius: 6,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: commissionEarned ? '#10b981' : '#f59e0b',
                    borderRadius: 6,
                    background: commissionEarned 
                      ? 'linear-gradient(90deg, #10b981, #16a34a)'
                      : 'linear-gradient(90deg, #f59e0b, #d97706)',
                    boxShadow: commissionEarned 
                      ? '0 2px 8px rgba(16, 185, 129, 0.4)'
                      : '0 2px 8px rgba(245, 158, 11, 0.4)'
                  }
                }} 
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>
                  $0
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>
                  ${commissionThreshold.toLocaleString()} Target
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.8)', 
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  Current Sales: ${(currentMonthData?.sales || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Commission locks automatically on the 1st of next month
                </Typography>
              </Box>
              {commissionEarned && (
                <Chip
                  icon={<CheckIcon />}
                  label={`$${commissionAmount.toLocaleString()} Earned`}
                  sx={{ 
                    bgcolor: '#16a34a', 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    px: 2,
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)'
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Orders Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            p: 4, 
            borderBottom: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#0d9488', mr: 2 }}>
                  <ReportsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Recent Orders
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {getTimeFilterLabel()} • {repOrders.length} orders
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="Live Data"
                sx={{ 
                  bgcolor: '#dcfce7', 
                  color: '#16a34a', 
                  fontWeight: 600,
                  border: '1px solid #16a34a'
                }}
              />
            </Box>
          </Box>
          {repOrders.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#f3f4f6', color: '#6b7280', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                <ReportsIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No orders found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your time filter to view orders from different periods
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ 
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  '& .MuiTableCell-head': {
                    borderBottom: '2px solid #e2e8f0'
                  }
                }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Clinic</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {repOrders.slice(0, 10).map((order, index) => {
                    const clinic = mockClinics.find(c => 
                      c.companyId === order.customerId || c.id === order.customerId
                    );
                    const company = mockCompanies.find(comp => comp.id === clinic?.companyId);
                    
                    return (
                      <TableRow 
                        key={order.id} 
                        sx={{ 
                          '&:hover': { 
                            bgcolor: '#f0fdfa',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease'
                          },
                          transition: 'all 0.2s ease',
                          borderLeft: index < 3 ? '4px solid #0d9488' : 'none'
                        }}
                      >
                        <TableCell sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                          {order.id}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.25 }}>
                              {clinic?.name || 'Unknown Clinic'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              {company?.name || 'Unknown Company'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                            {order.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {order.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                            ${order.totalAmount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status}
                            size="small"
                            sx={{
                              bgcolor: order.status === 'Completed' ? '#dcfce7' : 
                                     order.status === 'Processing' ? '#fef3c7' : '#fee2e2',
                              color: order.status === 'Completed' ? '#16a34a' : 
                                    order.status === 'Processing' ? '#d97706' : '#dc2626',
                              fontWeight: 700,
                              border: order.status === 'Completed' ? '1px solid #16a34a' : 
                                     order.status === 'Processing' ? '1px solid #d97706' : '1px solid #dc2626',
                              textTransform: 'uppercase',
                              fontSize: '0.65rem',
                              letterSpacing: 0.5
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                            {new Date(order.orderDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {repOrders.length > 10 && (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center', 
              borderTop: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
            }}>
              <Button 
                variant="contained" 
                size="large"
                sx={{
                  bgcolor: '#0d9488',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)',
                  '&:hover': {
                    bgcolor: '#0f766e',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(13, 148, 136, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                View All {repOrders.length} Orders
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RepDashboard;